import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getVenueByHost, getVenueById } from "./api";
import { usePreviewOverlay } from "./preview-overlay";
import { accentForegroundFor } from "./contrast";
import { googleWeightsFor } from "./font";
import type { Venue, VenueResponse } from "./types";

const KNOWN_FONTS = new Set([
  "JetBrains Mono",
  "Inter",
  "Space Grotesk",
  "Syne",
  "DM Sans",
  "Plus Jakarta Sans",
  "Source Sans 3",
]);

// Luminance check, matches src/lib/color-utils.ts in the Next.js app.
function isLightColor(hex: string): boolean {
  const m = hex.replace(/^#/, "");
  if (m.length !== 6) return false;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
}

function applyVenueTheme(venue: Venue) {
  const root = document.documentElement.style;
  if (venue.accentColor) root.setProperty("--accent", venue.accentColor);
  if (venue.backgroundColor) {
    root.setProperty("--bg", venue.backgroundColor);
    // Body text contrasts the BACKGROUND (dark bg → light text, light bg →
    // dark text), mirroring the Next.js venue layout. Must NOT be driven by
    // textColor, that's the on-accent colour and would black out body text
    // on a dark background.
    root.setProperty(
      "--fg",
      isLightColor(venue.backgroundColor) ? "#0a0a0a" : "#ededed",
    );
  }
  // textColor is the on-accent text colour (buttons/pills), but venues often
  // set it as their BODY text colour (e.g. dark text + dark accent), which
  // would paint black-on-black buttons. CONTRAST GUARD: only honour textColor
  // when it sits on the opposite luminance side of the accent; otherwise
  // derive a legible colour from the accent itself.
  root.setProperty("--accent-fg", accentForegroundFor(venue.accentColor, venue.textColor));
  if (venue.fontFamily) {
    root.setProperty(
      "--venue-font",
      `'${venue.fontFamily}', ui-monospace, monospace`,
    );
    if (KNOWN_FONTS.has(venue.fontFamily)) {
      ensureGoogleFont(venue.fontFamily);
    }
  }
  // Body font (Tyler @ Toka 2026-09-23): paired with the heading font so
  // a display heading can sit above a cleaner reading font. Exposed as
  // `--venue-body-font`; falls back to `--venue-font` when unset so nothing
  // changes for venues that only pick one family.
  const bodyFont = venue.appConfig?.secondaryFontFamily;
  if (bodyFont) {
    root.setProperty(
      "--venue-body-font",
      `'${bodyFont}', ui-monospace, monospace`,
    );
    if (KNOWN_FONTS.has(bodyFont)) {
      ensureGoogleFont(bodyFont);
    }
  } else {
    root.removeProperty("--venue-body-font");
  }
  // Per-venue browser favicon (+ apple-touch-icon): point the tab icon at the
  // venue's own icon so e.g. the Madruga tab shows Madruga, not the Assembly
  // triangle. Falls back to the default when the venue has no icon.
  if (venue.appIconUrl) {
    const icons = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');
    if (icons.length) {
      icons.forEach((l) => {
        l.href = venue.appIconUrl!;
        l.removeAttribute("type"); // let the browser sniff (png/svg)
      });
    } else {
      const l = document.createElement("link");
      l.rel = "icon";
      l.href = venue.appIconUrl;
      document.head.appendChild(l);
    }
    let touch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (!touch) {
      touch = document.createElement("link");
      touch.rel = "apple-touch-icon";
      document.head.appendChild(touch);
    }
    touch.href = venue.appIconUrl;
  }
}

function ensureGoogleFont(family: string) {
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const slug = family.replace(/\s+/g, "+");
  const wght = googleWeightsFor(family).join(";");
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${slug}:wght@${wght}&display=swap`;
  document.head.appendChild(link);
}

// Hook that loads the current venue (by hostname) and applies its theme to the
// document. All venue pages call this so the theme survives client-side route
// changes.
// Resolve the venue host. In dev only, a `?host=` query param overrides the
// real host so you can preview any venue locally (against the staging API via
// the Vite proxy) with hot-reload, e.g.
//   localhost:5173/festival/<id>?host=madruga.aws-staging.assemblytickets.com
// The override is gated on import.meta.env.DEV so it never applies in prod.
function resolvePreviewHost(): string {
  if (typeof window === "undefined") return "";
  if (import.meta.env.DEV) {
    const override = new URLSearchParams(window.location.search).get("host");
    if (override) return override;
  }
  return window.location.host;
}

export function useVenue() {
  const host = resolvePreviewHost();
  const cacheKey = `assembly:venue:${host}`;
  // Live-draft preview: when this site runs inside the admin's preview iframe,
  // unsaved editor changes arrive as an appConfig overlay (preview-overlay.ts)
  // and are merged over the fetched config below, so the preview reflects
  // keystrokes instantly without writing anything.
  const overlay = usePreviewOverlay();
  const query = useQuery({
    queryKey: ["venue", host],
    queryFn: () => getVenueByHost(host),
    enabled: !!host,
    // Seed from the last-known venue for this host so the themed app paints
    // instantly on relaunch (no loading flash before the green), then refetch
    // in the background. The white-label native apps always load the same
    // venue, so this is a cache hit on every launch after the first.
    initialData: (): VenueResponse | undefined => {
      try {
        const c = typeof window !== "undefined" ? localStorage.getItem(cacheKey) : null;
        return c ? (JSON.parse(c) as VenueResponse) : undefined;
      } catch {
        return undefined;
      }
    },
    initialDataUpdatedAt: 0, // treat the cached copy as stale → background refresh
  });

  useEffect(() => {
    if (query.data?.venue) {
      applyVenueTheme(query.data.venue);
      try {
        localStorage.setItem(cacheKey, JSON.stringify(query.data));
      } catch {
        /* storage unavailable, non-fatal */
      }
    }
  }, [query.data?.venue, cacheKey]);

  // Apply the preview overlay (draft-over-saved, key-level merge). Memoised so
  // consumers don't re-render on unrelated changes. No-op outside the preview.
  const data = useMemo(() => {
    if (!query.data?.venue || !overlay) return query.data;
    return {
      ...query.data,
      venue: {
        ...query.data.venue,
        appConfig: { ...(query.data.venue.appConfig ?? {}), ...overlay },
      },
    };
  }, [query.data, overlay]);

  return { ...query, data, host };
}

export function initialFor(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "·";
}

/**
 * useEditingVenue(targetVenueId?) , venue data for admin surfaces that
 * can target an ARBITRARY tenant (not just the current host).
 *
 * FIRE London Phase 1: the website builder on firelondon lets Harry
 * pick any child brand (Lightbox / Reggaeton / ...) and edit THAT
 * brand's site config, without full-page navigating to the child's
 * host. The hook delegates to useVenue() when no targetVenueId is
 * supplied (the normal case for every other admin page + every flat
 * client), so existing behavior is unchanged.
 *
 * Returns the SAME shape as useVenue() , `{ data, host, ...queryState }`
 * , so admin pages can swap useVenue() -> useEditingVenue() without
 * rewriting every downstream field read. When editing a different
 * brand, `host` is the TARGET brand's host (useful for preview iframe
 * src), not window.location.host.
 */
export function useEditingVenue(targetVenueId?: string | null) {
  const current = useVenue();

  // When no target is set, we ARE the current host, nothing new to fetch.
  const query = useQuery({
    queryKey: ["venue-by-id", targetVenueId ?? "__current__"],
    queryFn: () => getVenueById(targetVenueId!),
    enabled: !!targetVenueId,
    staleTime: 30_000,
    retry: false,
  });

  if (!targetVenueId) return current;

  // Compute the "effective host" for the target brand so iframe
  // previews + admin API calls can point at the right place. Prefer
  // appCustomDomain; fall back to <appSubdomain>.<current-stack-suffix>
  // so a child without a vanity DNS still resolves to its own host.
  const venue = query.data?.venue ?? null;
  const effectiveHost =
    venue?.appCustomDomain ??
    (venue?.appSubdomain
      ? `${venue.appSubdomain}.${stackSuffixOf(current.host)}`
      : current.host);

  return {
    ...query,
    data: query.data ?? null,
    host: effectiveHost,
  } as typeof current;
}

// Given the hub admin's current window host, return the "stack suffix"
// (aws-staging.assemblytickets.com or aws-prod.assemblytickets.com) so
// a target brand's stack-subdomain host can be constructed.
//
// Peeling the first label off a clean apex (e.g. firelondon.assemblytickets.com
// -> assemblytickets.com) is WRONG: the child brands' vanity DNS doesn't
// exist until their own Terraform entry ships (part of each client's
// setup deliverable). So onnation.assemblytickets.com has no DNS, but
// onnation.aws-prod.assemblytickets.com DOES (served by the stack
// wildcard). Fenton 2026-10-15 Chrome error on FIRE London preview
// iframe. Fix: anything that isn't staging falls through to the prod
// stack suffix.
function stackSuffixOf(host: string): string {
  if (host.endsWith(".aws-staging.assemblytickets.com") || host === "aws-staging.assemblytickets.com") {
    return "aws-staging.assemblytickets.com";
  }
  return "aws-prod.assemblytickets.com";
}
